import type { FunctionComponent } from 'react';
import { useState } from 'react';
import { Checkbox, Divider, Flex, Space, Stack, Text, Tooltip } from '@mantine/core';

import { isCheckboxValue } from 'helpers/menuPreview';
import type { ModifierGroupFormValue, PreviewModifier } from 'helpers/menuPreview';

import { ModifierPrice } from './ModifierPrice';
import { NestedModifierIndicator } from './NestedModifierIndicator';

type CheckboxGroupItemProps = {
  onModifierChange: (
    groupKey: string,
    selectedModifiers: Array<{ key: string; price: number }>,
  ) => void;
  onDrillIn: (modifierKey: string, modifierName: string) => void;
  modifiers: PreviewModifier[];
  groupValue?: ModifierGroupFormValue;
  groupKey: string;
  showTrailingDivider?: boolean;
};

export const CheckboxGroupItem: FunctionComponent<CheckboxGroupItemProps> = ({
  onModifierChange,
  onDrillIn,
  modifiers,
  groupValue,
  groupKey,
  showTrailingDivider = true,
}) => {
  const [selectedValues, setSelectedValues] = useState<string[]>(
    groupValue && isCheckboxValue(groupValue) ? groupValue.map(selected => selected.key) : [],
  );

  const handleCheckboxChange = (values: string[]) => {
    const newlyChecked = values.filter(key => !selectedValues.includes(key));
    setSelectedValues(values);

    const selectedModifiers = values.map(key => {
      const modifier = modifiers.find(mod => mod.key === key);
      return { key, price: modifier?.price || 0 };
    });

    onModifierChange(groupKey, selectedModifiers);

    const newlyCheckedWithNestedGroups = modifiers.find(
      modifier => newlyChecked.includes(modifier.key) && modifier.hasNestedGroups,
    );
    if (newlyCheckedWithNestedGroups) {
      onDrillIn(newlyCheckedWithNestedGroups.key, newlyCheckedWithNestedGroups.name ?? '');
    }
  };

  return (
    <>
      <Checkbox.Group value={selectedValues} onChange={handleCheckboxChange}>
        <Stack mb="sm">
          {modifiers.map(modifier => (
            <div key={modifier.uuid}>
              <Flex justify="space-between" align="center" gap="xs">
                <Stack gap={0} style={{ flexGrow: 1 }}>
                  {modifier.paused ? (
                    <Tooltip label="This modifier is unavailable">
                      <Text c="var(--content-secondary)">{modifier.name}</Text>
                    </Tooltip>
                  ) : (
                    <Text>{modifier.name}</Text>
                  )}
                  <ModifierPrice modifier={modifier} />
                </Stack>
                <Flex align="center" gap="xs">
                  <Checkbox
                    value={modifier.key}
                    disabled={modifier.paused}
                    aria-label={modifier.name}
                  />
                  <NestedModifierIndicator
                    variant="inline"
                    hasNestedGroups={modifier.hasNestedGroups ?? false}
                    isSelected={selectedValues.includes(modifier.key)}
                    onDrillIn={() => onDrillIn(modifier.key, modifier.name ?? '')}
                  />
                </Flex>
              </Flex>
              <NestedModifierIndicator
                variant="banner"
                hasNestedGroups={modifier.hasNestedGroups ?? false}
                isSelected={selectedValues.includes(modifier.key)}
                nestedSummary={modifier.nestedSummary}
                onDrillIn={() => onDrillIn(modifier.key, modifier.name ?? '')}
              />
            </div>
          ))}
        </Stack>
      </Checkbox.Group>
      {showTrailingDivider ? <Divider my="md" /> : <Space h="xl" />}
    </>
  );
};
